import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        link: 'text-primary underline-offset-4 hover:underline',
        primary: `border font-semibold
          bg-button-primary-bg-600 text-button-primary-fg-white border-button-primary-bg-600
          hover:border-button-primary-border-hover hover:bg-button-primary-bg-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-bg-disabled`,
        error: `border font-semibold
          bg-button-error-bg-600 border-button-error-bg-600 text-button-primary-fg-white
          hover:bg-button-error-bg-hover hover:border-button-error-border-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-bg-disabled`,
        warning: `border font-semibold
          bg-button-warning-bg-600 border-button-warning-bg-600 text-button-primary-fg-white
          hover:bg-button-warning-bg-hover hover:border-button-warning-border-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-bg-disabled`,
        success: `border font-semibold
          bg-button-success-bg-600 border-button-success-bg-600  text-button-primary-fg-white
          hover:border-button-success-border-hover hover:bg-button-success-bg-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-bg-disabled`,
        outlinePrimary: `border font-semibold
          bg-transparent border-button-color-primary-color-border text-button-primary-color-fg-white
          hover:border-button-outline-primary-color-border-hover hover:text-button-primary-color-fg-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-bg-disabled`,
        outlineError: `border font-semibold
          bg-button-error-color-bg-600 border-button-error-color-border text-button-error-color-fg-white
          hover:border-button-outline-error-color-border-hover hover:bg-button-error-color-bg-hover hover:text-button-error-color-fg-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-bg-disabled`,
        outlineWarning: `border font-semibold
          bg-button-warning-color-bg-600 border-button-warning-color-border-hover text-button-warning-color-fg-white
          hover:bg-button-warning-color-bg-hover hover:border-button-warning-color-border-hover hover:text-button-warning-color-fg-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-bg-disabled`,
        outlineSuccess: `border font-semibold
          bg-button-outline-success-color-bg border-button-outline-success-color-border text-button-outline-success-color-fg-white
          hover:border-button-outline-success-color-border-hover hover:bg-button-outline-success-color-bg-hover hover:text-button-outline-success-color-fg-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-bg-disabled`,
        outlineGray: `border font-semibold
          bg-transparent border-button-gray-border text-button-gray-fg-700
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-bg-disabled`,
        ghostPrimary: `font-semibold
          bg-transparent border-button-color-primary-color-border text-button-primary-color-fg-white
          hover:border-button-outline-primary-color-border-hover hover:text-button-primary-color-fg-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-transparent`,
        ghostError: `font-semibold
          bg-button-error-color-bg-600 border-button-error-color-border text-button-error-color-fg-white
          hover:border-button-outline-error-color-border-hover hover:text-button-error-color-fg-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-transparent`,
        ghostWarning: `font-semibold
          bg-button-warning-color-bg-600 border-button-warning-color-border-hover text-button-warning-color-fg-white
          hover:border-button-warning-color-border-hover hover:text-button-warning-color-fg-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-transparent`,
        ghostSuccess: `font-semibold
          bg-button-outline-success-color-bg border-button-outline-success-color-border text-button-outline-success-color-fg-white
          hover:border-button-outline-success-color-border-hover hover:text-button-outline-success-color-fg-hover
          disabled:border-border-disabled-300 disabled:text-fg-disabled-300 disabled:bg-transparent`,
        ghostGray: `font-semibold
          bg-transparent border-transparent text-button-gray-fg-700
          disabled:bg-transparent`,
      },
      size: {
        ghost: 'h-auto p-0',
        xxs: 'px-3 py-1.5 h-7 gap-1',
        xs: 'px-3 py-2 h-8 gap-1',
        sm: 'px-3 py-2.5 h-10 gap-1',
        md: 'px-3.5 py-3 h-11 gap-1',
        lg: 'px-4 py-3 h-11 gap-1.5',
        xl: 'px-[18px] py-[14px] h-[48px] gap-1.5',
        '2xl': 'px-[22px] py-[17px] h-[56px] gap-[10px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

function Button({
  className,
  variant = 'primary',
  size = 'md',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
